import classNames from 'classnames';
import moment from 'moment';
import React, { ChangeEvent, ChangeEventHandler, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toastr } from 'react-redux-toastr';

import ExportIcon from 'src/assets/hosLogs/export.svg';
import { isDesktop } from 'src/utils/devices';
import { Button } from 'src/components/Button';
import { Modal } from 'src/components/Modal/Modal';
import { AppButton } from 'src/components/AppButton/AppButton';
import { exportAllHosLogsAction, fetchHosExportLinkAction } from 'src/features/hosLogs/store';
import { startLoading, stopLoading } from 'src/features/loading/loadingStore';
import { useInterval } from 'src/utils/useInterval';
import { downloadFile, printFile } from 'src/utils/hosExport';

import './ExportAllLogs.scss';
import { getHosExportJobId, getHosExportLink, setHosExportJobId, setHosExportLink } from '../store';

interface ExportAllLogsProps {
  hosLogsLength: number;
  hosLogsStartDate: string;
  hosLogsEndDate: string;
  employeeId: number;
}

interface ExportOptionProps {
  isSelected: boolean;
  isLoadingExport: boolean;
  title: string;
  subtext: string;
  value: string;
  onChange: ChangeEventHandler;
}

const HOS_EXPORT_OPTIONS = {
  print: 'print',
  download: 'download',
  email: 'email',
};

type LinkResolverOptions = {
  selectedOption: string;
  url: string;
};
const resolveHosExportLink = ({ url, selectedOption }: LinkResolverOptions) => {
  const exportFilename = `Maven Machines-HOS Report-${moment().format('MM-DD-YYYY')}.pdf`;
  switch (selectedOption) {
    case HOS_EXPORT_OPTIONS.download:
      downloadFile(url, exportFilename);
      break;
    case HOS_EXPORT_OPTIONS.print:
      printFile(url);
      break;
  }
};

const HosExportOption: React.FC<ExportOptionProps> = props => {
  return (
    <li>
      <label>
        <input
          type="radio"
          checked={props.isSelected}
          disabled={props.isLoadingExport}
          value={props.value}
          name="selectedOption"
          onChange={props.onChange}
        />
        <div className="label">
          <div className="title"> {props.title} </div>
          <p> {props.subtext} </p>
        </div>
      </label>
    </li>
  );
};

export const ExportAllLogs: React.FC<ExportAllLogsProps> = ({
  hosLogsLength,
  hosLogsStartDate,
  hosLogsEndDate,
  employeeId,
}) => {
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [emailValue, setEmailValue] = useState<string>('');
  const [isLoadingExportLink, setIsLoadingExportLink] = useState<boolean>(false);
  const [isValidEmail, setIsValidEmail] = useState<boolean>(true);
  const [selectedOption, setSelectedOption] = useState<string>('');
  const isOptionSelected = (option: string) => {
    return selectedOption === option;
  };

  const dispatch = useDispatch();

  const hosExportJobId = useSelector(getHosExportJobId);
  const hosExportLink = useSelector(getHosExportLink);

  // Reset all state related to the last-fetched export
  const resetExportState = () => {
    dispatch(
      setHosExportJobId({
        jobId: '',
      }),
    );
    dispatch(
      setHosExportLink({
        reportUrl: '',
      }),
    );
    setSelectedOption('');
    setShowExportModal(false);
    setIsLoadingExportLink(false);
  };

  useInterval(() => {
    if (selectedOption.length && hosExportJobId.length) {
      dispatch(fetchHosExportLinkAction({ jobId: hosExportJobId }));

      if (hosExportLink?.length && hosExportLink.includes(hosExportJobId)) {
        toastr.success('Export Complete', 'Your HOS export is ready', { timeOut: 8000 });

        resolveHosExportLink({ url: hosExportLink, selectedOption });
        resetExportState();
      }
    }
  }, 3000);

  const resetEmailInput = useCallback(() => {
    setEmailValue('');
    setIsValidEmail(true);
  }, []);

  const toggleExportModalHandler = useCallback(() => {
    setShowExportModal(prev => !prev);
    resetEmailInput();
  }, [resetEmailInput]);

  const onChangeEmailInput = useCallback(
    () => (e: ChangeEvent<HTMLInputElement>) => {
      const emailRegExp = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
      setEmailValue(e.target.value);
      setIsValidEmail(!emailRegExp.test(e.target.value));
    },
    [],
  );
  const onSendEmailValidation = () => {
    return isOptionSelected(HOS_EXPORT_OPTIONS.email) && isValidEmail;
  };

  const onSendHandler = useCallback(() => {
    dispatch(
      exportAllHosLogsAction({
        startDate: hosLogsStartDate,
        endDate: hosLogsEndDate,
        emails: emailValue ? [emailValue] : [],
        employeeIds: [employeeId],
        includeViolations: true,
        requesterId: employeeId,
        deliveryFormat: selectedOption,
      }),
    );

    setIsLoadingExportLink(true);
    resetEmailInput();
  }, [
    emailValue,
    selectedOption,
    resetEmailInput,
    hosLogsStartDate,
    hosLogsEndDate,
    employeeId,
    dispatch,
  ]);

  const handleOptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value);
  };

  const buttonClassNames = classNames('hos-logs__button-export', { 'is-desktop': isDesktop });

  const deliveryMethods = [
    {
      isSelected: isOptionSelected(HOS_EXPORT_OPTIONS.download),
      isLoadingExport: isLoadingExportLink,
      subtext: 'A PDF file will download to your computer. Maximum of 200 daily logs.',
      title: 'Download HOS Report',
      onChange: handleOptionChange,
      value: HOS_EXPORT_OPTIONS.download,
    },
    {
      isSelected: isOptionSelected(HOS_EXPORT_OPTIONS.print),
      isLoadingExport: isLoadingExportLink,
      subtext: 'A PDF file will open in your browser for you to print. Maximum of 200 daily logs.',
      title: 'Print HOS Report',
      onChange: handleOptionChange,
      value: HOS_EXPORT_OPTIONS.print,
    },
    {
      isSelected: isOptionSelected(HOS_EXPORT_OPTIONS.email),
      isLoadingExport: isLoadingExportLink,
      subtext:
        'An email with the HOS Report will be sent to the below users. Maximum of 3,000 daily logs.',
      title: 'Email HOS Report',
      onChange: handleOptionChange,
      value: HOS_EXPORT_OPTIONS.email,
    },
  ];

  return (
    <>
      <Button
        title="Export All Logs"
        icon={ExportIcon}
        className={buttonClassNames}
        onClick={toggleExportModalHandler}
        disabled={!hosLogsStartDate || !hosLogsEndDate}
      />
      {showExportModal && (
        <Modal
          onCloseDialog={toggleExportModalHandler}
          confirmButton="Confirm"
          disableCancelButton={isLoadingExportLink}
          header="Export Hours of Service Records"
          description={`You have requested a PDF export of ${hosLogsLength} HOS Logs.`}
          showConfirmButton={false}
          className="export-all-logs__modal"
        >
          <div className="export-all-logs__email-box-wrapper">
            <div className="export-all-logs__email-box">
              <ul className="delivery-method-list">
                {deliveryMethods.map(props => (
                  <HosExportOption key={props.title} {...props} />
                ))}
              </ul>
            </div>

            {isOptionSelected(HOS_EXPORT_OPTIONS.email) && (
              <div className="export-all-logs__email-box">
                <input
                  className="export-all-logs__email-input"
                  type="email"
                  placeholder="Type email"
                  value={emailValue}
                  onChange={onChangeEmailInput()}
                />
              </div>
            )}
          </div>

          <AppButton
            title={isLoadingExportLink ? 'Exporting' : 'Export'}
            onClick={onSendHandler}
            className="export-logs-app-btn"
            small
            disabled={!selectedOption || onSendEmailValidation() || isLoadingExportLink}
          />
        </Modal>
      )}
    </>
  );
};
